import { DocumentData } from "../../types";

/*
 Il nome con cui un attestato arriva sul computer di chi lo scarica.

 Non è quello archiviato in DocuManager: quello è il nome del file caricato a suo tempo, e
 sui dati veri è illeggibile o incompleto — `SSBI04P002_703_1474_BROGI_CARLO_WE_TECH'SPA.pdf`
 accanto a `SSBI04P001_CARLO_BROGI`, che non ha nemmeno l'estensione e che il sistema
 operativo non sa quindi con che programma aprire.

 Qui si costruisce un nome che dice di chi è, di che corso e fino a quando vale, nell'ordine
 in cui serve a chi raccoglie gli attestati per un'ispezione:

   ATTESTATO_BROGI-CARLO_SICUREZZA-LAVORATORI-PARTE-GENERALE_2024-11-28.pdf

 La data in fondo è quella di scadenza in forma ISO, così i file di una stessa persona si
 ordinano da soli nella cartella. Ogni pezzo che manca viene saltato invece di lasciare un
 buco: i metadati degli attestati sono estratti da un modello linguistico e mancano spesso
 (ADR-0006 del backend).
*/

const ESTENSIONE_PREDEFINITA = "pdf";

const ESTENSIONI_PER_TIPO: Record<string, string> = {
  "application/pdf": "pdf",
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/png": "png",
  "image/tiff": "tif",
};

// I caratteri che Windows non ammette in un nome di file, più gli apici, che
// sopravvivono ma costringono a virgolettare il nome da riga di comando.
const CARATTERI_VIETATI = /[\\/:*?"<>|'`]/g;

// Oltre questa lunghezza il nome non aggiunge informazione e comincia a dare
// fastidio ai percorsi lunghi di Windows.
const LUNGHEZZA_MASSIMA = 120;

export function nomeFileAttestato(attestato: DocumentData): string {
  const metadata = attestato.metadata;
  const discente = pulisci(
    [metadata?.cognome_discente, metadata?.nome_discente]
      .filter(Boolean)
      .join(" ") || metadata?.cf_discente
  );
  const corso = pulisci(metadata?.titolo_corso || metadata?.codice_corso);
  const scadenza = dataIso(metadata?.data_scadenza);

  const parti = ["ATTESTATO", discente, corso, scadenza].filter(
    (parte): parte is string => Boolean(parte)
  );
  // Restano solo "ATTESTATO" e nient'altro: senza un solo metadato leggibile è
  // più onesto il nome archiviato, che almeno distingue un file dall'altro.
  const nome =
    parti.length > 1
      ? parti.join("_").slice(0, LUNGHEZZA_MASSIMA)
      : pulisci(senzaEstensione(attestato.fileName)) ||
        `ATTESTATO-${attestato.id}`;

  return `${nome}.${estensione(attestato)}`;
}

/*
 Il nome dell'archivio con cui si scaricano più attestati insieme. Quando sono tutti della
 stessa persona lo dice, perché è il caso normale — si scaricano gli attestati di un dipendente
 dalla sua scheda; altrimenti resta la data, che almeno distingue due scaricamenti fatti in
 giorni diversi.

 Lo zip lo costruisce il backend, che lo nomina allo stesso modo nell'intestazione
 Content-Disposition: quell'intestazione però al browser non arriva (risposta cross-origin),
 quindi il nome va ricomposto anche qui.
*/
export function nomeFileArchivioAttestati(
  attestati: DocumentData[],
  oggi: Date = new Date()
): string {
  const discenti = new Set(
    attestati
      .map((a) =>
        pulisci(
          [a.metadata?.cognome_discente, a.metadata?.nome_discente]
            .filter(Boolean)
            .join(" ") || a.metadata?.cf_discente
        )
      )
      .filter((discente): discente is string => Boolean(discente))
  );
  const coda =
    discenti.size === 1
      ? Array.from(discenti)[0]
      : dataIso(oggi) ?? String(oggi.getFullYear());
  return `ATTESTATI_${coda}.zip`;
}

/* Spazi e separatori diventano trattini, il resto sparisce: un nome di file, non una frase. */
function pulisci(valore: string | undefined): string | undefined {
  if (!valore) return undefined;
  const pulito = valore
    .replace(CARATTERI_VIETATI, " ")
    .trim()
    .replace(/[\s_]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-|-$/g, "");
  return pulito || undefined;
}

/* La sola data, senza orario e senza fuso: è una scadenza, non un istante. */
function dataIso(data: Date | undefined): string | undefined {
  if (!data) return undefined;
  const convertita = new Date(data);
  if (Number.isNaN(convertita.getTime())) return undefined;
  const mese = String(convertita.getMonth() + 1).padStart(2, "0");
  const giorno = String(convertita.getDate()).padStart(2, "0");
  return `${convertita.getFullYear()}-${mese}-${giorno}`;
}

/*
 L'estensione la decide il tipo del documento archiviato; se DocuManager non lo dice si
 guarda il nome archiviato, e in ultima istanza si assume il PDF, che è ciò che l'archivio
 contiene. Senza estensione il file scaricato non si apre con un doppio clic.
*/
function estensione(attestato: DocumentData): string {
  const daTipo = ESTENSIONI_PER_TIPO[(attestato.mimetype || "").toLowerCase()];
  if (daTipo) return daTipo;
  const daNome = (attestato.fileName || "").match(/\.([a-z0-9]{1,5})$/i);
  return daNome ? daNome[1].toLowerCase() : ESTENSIONE_PREDEFINITA;
}

function senzaEstensione(fileName: string | undefined): string | undefined {
  return fileName?.replace(/\.[a-z0-9]{1,5}$/i, "");
}
